import { useCallback, useEffect, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import { PublicStatusMapSettingsView } from '@workspace/silk-ui';

import {
  getTenantPublicMapSettings,
  updateTenantPublicMapSettings,
  type PublicMapSettings,
  type TenantPublicMapSettingsResponse,
} from '@/lib/public-map';
import { useStatusInventoryAccess } from '@/lib/useStatusInventoryAccess';

export default function PublicStatusMapSettingsPage() {
  const { activeTenant } = useScope();
  const access = useStatusInventoryAccess();
  const [config, setConfig] = useState<TenantPublicMapSettingsResponse | null>(null);
  const [draft, setDraft] = useState<PublicMapSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeTenant?.id || !access.accessEnabled) {
      setConfig(null);
      setDraft(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await getTenantPublicMapSettings(activeTenant.id);
      setConfig(next);
      setDraft(next.settings);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to load public status-map settings.');
    } finally {
      setLoading(false);
    }
  }, [access.accessEnabled, activeTenant?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    if (!activeTenant?.id || !draft || !config?.canManage) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await updateTenantPublicMapSettings(activeTenant.id, draft);
      setConfig(saved);
      setDraft(saved.settings);
      setNotice(saved.settings.statusMapEnabled ? 'Public status map enabled.' : 'Public status map disabled.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save public status-map settings.');
    } finally {
      setSaving(false);
    }
  }, [activeTenant?.id, config?.canManage, draft]);

  return (
    <PublicStatusMapSettingsView
      tenantLabel={activeTenant?.name ?? activeTenant?.id ?? 'the selected tenant'}
      enabled={Boolean(draft?.statusMapEnabled)}
      publicUrl={config?.sharedStatusSiteUrl}
      canManage={Boolean(config?.canManage)}
      accessDenied={!access.accessEnabled || !activeTenant?.id}
      loading={loading}
      saving={saving}
      error={error}
      notice={notice}
      onEnabledChange={enabled => setDraft(current => current ? { ...current, statusMapEnabled: enabled } : current)}
      onSave={() => void save()}
      onRefresh={() => void load()}
    />
  );
}
